import React from 'react'
import { TextField } from '@mui/material'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import Dialog from '../../styleguide/dialog/Dialog'
import DialogTitle from '../../styleguide/dialog/DialogTitle'
import DialogContent from '../../styleguide/dialog/DialogContent'
import DialogActions from '../../styleguide/dialog/DialogActions'
import Button from '../../styleguide/buttons/Button'
import theme from '../../styleguide/theme'

const DescriptionBox = styled.p`
	display: flex;
	padding: 24px 0;
	font-size: 14px;
	color: ${theme.palette.greyCustom.main};
`

export interface InputAlertWarningNoteDialogProps {
	isOpen: boolean
	warningNote: string
	hideBackdrop: boolean
	onNoteChange: (note: string) => void
	onSave: () => void
	onClose?: () => void
	instrument?: string
	fieldKey?: string
	index?: number
}

const INDEX_REGEX = /(?:\[(\d+)\]|\.(\d+)(?:\.|$))/

const InputAlertWarningNoteDialog = ({
	isOpen,
	warningNote,
	hideBackdrop,
	onNoteChange,
	onSave,
	instrument,
	fieldKey,
	index,
}: InputAlertWarningNoteDialogProps) => {
	const { t } = useTranslation()

	const getFieldInfoString = (): string => {
		if (!fieldKey) return ''

		const toWords = (camelCase: string) =>
			camelCase
				.replace(/([a-z])([A-Z])/g, '$1 $2')
				.replace(/^./, c => c.toUpperCase())

		if (instrument === 'contactLenses') {
			const match =
				fieldKey.match(/^\[(\d+)\]\.?measure\.(OD|OS)\.(.+)$/) ??
				fieldKey.match(/^\w+\[(\d+)\]\.(OD|OS)\.\w+\.(.+)$/)
			if (!match) return ''
			const productIndex = parseInt(match[1]) + 1
			const eye = match[2]
			const rawField = match[3]
			const displayField = t(`lensesTrial.product.headers.${rawField}`, {
				defaultValue: toWords(rawField),
			})
			return `${t(
				'doctor.contactLenses',
			)} #${productIndex} ${eye} - ${displayField}`
		}

		const indexSuffixMatch = fieldKey.match(INDEX_REGEX)
		const lowerCaseKey = fieldKey.toLocaleLowerCase()
		const cleanKey = indexSuffixMatch
			? fieldKey.slice(0, indexSuffixMatch.index)
			: fieldKey

		const parts = cleanKey.split('.')
		const rawLastPart =
			fieldKey
				.split('.')
				.pop()
				?.replace(/\[.*\]$/, '') ?? ''
		const lastPart = rawLastPart || parts[parts.length - 1]
		if (!lastPart) return ''

		const rawIndex = indexSuffixMatch
			? parseInt(indexSuffixMatch[1] ?? indexSuffixMatch[2])
			: null
		const entryIndex = rawIndex !== null ? rawIndex + 1 : null

		const isTonometry = lowerCaseKey.includes('iop')
		const iopIndex = lowerCaseKey.indexOf('iop')
		const formattedField = isTonometry
			? 'IOP' + fieldKey.slice(iopIndex + 3)
			: lastPart.charAt(0).toUpperCase() + lastPart.slice(1)

		const rawSection = isTonometry
			? 'Tonometry'
			: instrument && instrument !== 'FormattedInstrument'
			? instrument
			: ''

		const eyeMatch = fieldKey.match(/\b(OD|OS)\b/)
		const eye = eyeMatch?.[1] ?? ''

		const isHistoricalRxOrLensometry =
			instrument && ['historicalRx', 'lensometry'].includes(instrument)
		const isInstrumentField = instrument === 'InstrumentField'

		if (isHistoricalRxOrLensometry || isInstrumentField) {
			const entityName = toWords(parts[0])
			const eyePart = parts[3]
			const entityEye = eyePart === 'OD' || eyePart === 'OS' ? eyePart : ''
			const fieldLabels = {
				sphere: 'Sphere',
				cyl: 'Cylinder',
				axis: 'Axis',
			}
			const displayName =
				fieldLabels[lastPart as keyof typeof fieldLabels] ?? formattedField

			const activeIndex =
				isHistoricalRxOrLensometry && index !== undefined
					? index + 1
					: entryIndex
			const indexLabel = activeIndex !== null ? ` #${activeIndex}` : ''

			return entityEye
				? `${entityName}${indexLabel} ${entityEye} - ${displayName}`
				: `${entityName}${indexLabel} - ${displayName}`
		}

		const indexMatch = fieldKey.match(INDEX_REGEX)

		if (indexMatch) {
			const sectionName = rawSection ? toWords(rawSection) : ''
			const prefix =
				eye && fieldKey.includes('LensData')
					? sectionName
						? `${sectionName} ${eye}`
						: eye
					: [sectionName ? `${sectionName} ${entryIndex}` : '', eye]
							.filter(Boolean)
							.join(' ')
			return prefix ? `${prefix} - ${formattedField}` : formattedField
		}

		const sectionBase = rawSection || (parts[0] !== eye ? parts[0] : '')
		const sectionName = sectionBase ? toWords(sectionBase) : ''
		const prefix = [sectionName, eye].filter(Boolean).join(' ')
		return prefix ? `${prefix} - ${formattedField}` : formattedField
	}

	const fieldInfoString = getFieldInfoString()

	return (
		<Dialog
			open={isOpen}
			hideBackdrop={hideBackdrop}
			PaperProps={{
				sx: {
					boxShadow: 'none',
				},
			}}
		>
			<DialogTitle>{t('inputAlert.warningNote.title')}</DialogTitle>
			<DialogContent sx={{ paddingTop: '1rem' }}>
				<DescriptionBox>
					{t('inputAlert.warningNote.description')}
				</DescriptionBox>
				{fieldInfoString && <DescriptionBox>{fieldInfoString}</DescriptionBox>}
				<TextField
					fullWidth
					multiline
					rows={3}
					value={warningNote}
					onChange={e => onNoteChange(e.target.value)}
					placeholder={t('inputAlert.warningNote.placeholder')}
					variant="outlined"
				/>
			</DialogContent>
			<DialogActions>
				<Button variant="contained" onClick={onSave} disabled={!warningNote}>
					{t('app.save')}
				</Button>
			</DialogActions>
		</Dialog>
	)
}

export default InputAlertWarningNoteDialog
